import React, { useState } from 'react';
import getColor, { Color } from '../components/utils/getColor';

import Img from 'gatsby-image';
import styled from 'styled-components';
import CTA from '../components/CTA';
import Container from '../components/Container';
import Footer from '../components/Footer/Footer';
import HowItWorks from '../components/HowItWorks';
import Marquee from '../components/Marquee';
import getGradient from '../components/utils/getGradient';

import { PageProps } from 'gatsby';
import { SEO } from '../SEO';
import { Button } from '../components/Button';
import { Site } from '../components/Site';
import { Main } from '../components/Styled';
import { customers, partners } from '../constants';
import { useProducts } from '../hooks/useProducts';
import theme from '../theme';


enum Videos {
  Hero = 'hero',
  APM = 'apm',
  Scorecard = 'scorecard',
}

type State = {
  [key in Videos]: boolean;
};

const Product: React.FC<PageProps> = (props) => {
  const [loaded, setLoaded] = useState<State>({
    [Videos.Hero]: false,
    [Videos.APM]: false,
    [Videos.Scorecard]: false,
  });

  const data = useProducts();

  const handleLoad = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    if (e.target instanceof HTMLVideoElement && e.target.dataset.video !== undefined) {
      setLoaded({
        ...loaded,
        [e.target.dataset.video]: true,
      });
    }
  };

  return (
    <Site seo={SEO.product} {...props}>
      <Main>
        <Container>
          <HeroGrid>
            <HeroWrapper>
              <H1>daisee enables you to understand what your customers are saying</H1>
              <Copy hero sectionColor={Color.Blue}>
                Our solution provides business leaders with critical insights into their customers’ voice, surfacing
                opportunities, obstacles and risks like <Emphasis>never before.</Emphasis>
              </Copy>
            </HeroWrapper>
            <IntroImgWrapper>
              <VideoWrapper loaded={loaded[Videos.Hero]}>
                <video
                  preload="none"
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster="https://res.cloudinary.com/harrybu/video/upload/v1605065292/safari_hevc_sxdzta.jpg"
                  onPlay={(e) => handleLoad(e)}
                  data-video={Videos.Hero}
                >
                  <source
                    src="https://res.cloudinary.com/harrybu/video/upload/v1604314218/Comp_1_abd4yl.webm"
                    type="video/webm"
                  />
                  <source
                    src="https://res.cloudinary.com/harrybu/video/upload/v1605065908/safari_1_tlllwh.mp4"
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </VideoWrapper>
            </IntroImgWrapper>
          </HeroGrid>
        </Container>
        <Section id="how-it-works">
          <Container>
            <SectionHeading>How It Works</SectionHeading>
          </Container>
          <HowItWorks />
        </Section>
        <Container>
          <Section id="features">
            <SectionHeading>Features</SectionHeading>
            {/* Automated PROGRAMMABLE SCORECARD colour purple */}
            <IntroGrid id='scorecard'>
              <CopyWrapper>
                <Tag sectionColor={"#6F37BD"}>Automated PROGRAMMABLE SCORECARD</Tag>
                <H3>Get closer to the pulse of your business and ensure a flawless customer experience with our Programmable Scorecard™</H3>
                <Copy>
                  The world’s first Programmable Scorecard™ replaces the traditional paper scorecard with an automated digital solution that works in tandem with the Media Player.
                  <br />
                  <br />
                  Scorecards can be set to filter questions based on the type of call or team the agent is in to ensure the Scorecard is evaluating quality appropriate to the customer interaction.
                  <br />
                  <br />
                  Script Adherence is an integral component of our Programmable Scorecard and the rating weight can be set to ensure that problematic calls will be highlighted for rapid remediation.
                  <br />
                  <br />
                  These statements can be Verbatim – or an exact match of the words, or Non-Verbatim where they convey the understanding of the issue without having to use the exact words.
                </Copy>
              </CopyWrapper>
              <ImgWrapper >
                <StyledImg style={{zIndex: 2}} shadow fluid={data.ui3.childImageSharp.fluid} alt="Scorecards" />
                <Triangle />
              </ImgWrapper>
            </IntroGrid>

            {/* DAISEE ESSENCE colour pink-purple */}
            <IntroGrid>
              <ImgWrapper >
                <StyledImg style={{zIndex: 2}} shadow fluid={data.essence.childImageSharp.fluid} alt="Essence" />
                <Trapezium sectionColor={Color.Purple}/>
              </ImgWrapper>

              <CopyWrapper id='essence'>
                <Tag sectionColor={Color.Purple}>DAISEE ESSENCE</Tag>
                <H3>Discover the essence of your customers’ intent</H3>
                <Copy>
                  daisee Essence is a unique AI-powered feature that enables contact centres to better understand their customers' needs and reasons for contacting them. 
                  <br />
                  <br />
                  Using unsupervised machine learning, daisee Essence analyses every customer conversation and separates key categories and underlying drivers from general language to provide insights that can drive business decisions. This allows for a more efficient and purposeful use of human resources in the contact centre, as it eliminates the need for agents to manually update systems. It overcomes human bias and eliminates inaccurate data, providing a reliable view of the caller's intent and freeing up agents' time to focus on more important tasks. 
                  <br />
                  <br />
                  The high-resolution call trends are visualised in a simple user interface, making it easy to quantify trends, even the unexpected ones. daisee Essence provides a reliable view of the caller's intent, improving efficiency, reducing costs, and providing insights that can impact operational efficiency and customer experience.
                  <br />
                  <br />
                  daisee's most recent version of Essence now categorises the reason for the call into a product or service category, and then identifies the underlying root cause driver for each of these categories. This enriches the insights and provides even deeper analysis of the voice of the customer, which can help elevate the contact centre to a source of strategic value in driving competitive differentiation. With daisee Essence, businesses can elevate their contact centre to a source of strategic value and make informed decisions that impact operational efficiency and customer experience.
                </Copy>
                <a className='button' href={data.essenceFlyer.publicURL} download>
                  <Button
                    action='Download the daisee Essence flyer'
                    btnHeight='auto'
                    bgColor={theme.colors.purple}
                  />
                </a>
              </CopyWrapper>
            </IntroGrid>

            {/* DAISEE PREDICTORS colour: bright pink */}
            <IntroGrid>
              <CopyWrapper id='predictors'>
                <Tag sectionColor={Color.Pink}>DAISEE PREDICTORS</Tag>
                <H3>Embrace the power of predictive insights</H3>
                <Copy>
                  daisee Predictors is a cutting-edge feature that utilises the latest technologies in AI, machine learning and behavioural science to extract valuable predictive insights from customer interactions. With the ability to mine each interaction for sequences of words, voice tonality and sentiment, daisee Predictors can provide highly accurate predictions on customer complaints, churn, customer satisfaction and call outcomes.
                  <br />
                  <br />
                  Recently we have expanded the scope of predictive forecasting, with our models now boasting an accuracy rate of 75-85% when it comes to predicting customer complaints.
                  <br />
                  <br />
                  Our AI technology responds not only to words but to the relationship between words, which gives context to customer interactions and provides our customers with significantly enhanced predictive insights leading to tangible commercial outcomes. With our solution, businesses can make accurate predictions and take prompt action to increase customer satisfaction, reduce customer churn, anticipate closed sales, and ultimately, improve their commercial outcomes.
                  <br />
                  <br />
                  Our prediction model has been fine-tuned through millions of hours of call centre interactions, which have revealed valuable trends based on phrases, tone and emotions. With daisee Predictors, businesses can have the power of predictive insights at their fingertips, allowing them to take control of their customer interactions and enhance their customer experience.
                </Copy>
                <a className='button' href={data.predictorFlyer.publicURL} download>
                  <Button
                    action='Download the daisee PREDICTORS flyer'
                    btnHeight='auto'
                    bgColor={theme.colors.purple}
                  />
                </a>
              </CopyWrapper>
              <ImgWrapper>
                  <StyledImg style={{zIndex: 2}} shadow fluid={data.predictors.childImageSharp.fluid} alt="daisee Predictors" />
                  <ColourBlockRight sectionColor={Color.Pink}/>
              </ImgWrapper>
            </IntroGrid>

            {/* DAISEE SCORECARD BUILDER colour purple */}
            <IntroGrid>
              <ImgWrapper >
                <StyledImg style={{zIndex: 2}} shadow fluid={data.scorecardbuilder.childImageSharp.fluid} alt="Scorecard Builder" />
                <Trapezium sectionColor={"#6F37BD"}/>
              </ImgWrapper>

              <CopyWrapper id='scbuilder'>
                <Tag sectionColor={"#6F37BD"}>DAISEE SCORECARD BUILDER</Tag>
                <H3>Build and curate scorecards that reflect your business</H3>
                <Copy>
                  daisee Scorecard Builder empowers customers to fully customise the daisee automated scorecard themselves, allowing it to reflect the most pressing business drivers in a way that makes sense within their organisation. 
                  <br />
                  <br />
                  With just a few clicks and less than an hour of training, customers can produce feature-rich scorecards that showcase valuable and bespoke contact centre data. The highly customisable tool enables the daisee data collection and dashboard displays to be tweaked and tuned to best meet changing short and long-term business needs.
                  <br />
                  <br />
                  The daisee Scorecard Builder works by providing an easy-to-use interface within the daisee platform, with a library of resources pre-built that are tuned to specific industries and their key metrics. daisee's scorecard builder draws upon millions of contact centre interactions to power an ever-expanding list of data-science generated capabilities, which in turn can be incorporated into the daisee scorecard.                   
                </Copy>
              </CopyWrapper>
            </IntroGrid>
           
            {/* daisee report builder colour: yellow */}
            <IntroGrid>
              <CopyWrapper id='report'>
                <Tag sectionColor={Color.Yellow}>daisee report builder</Tag>
                <H3>Unlock the power of your data with custom reports</H3>
                <Copy>
                  daisee Report Builder empowers businesses to easily ask and answer their own bespoke questions without the need for data analytics teams. Traditionally, this type of work would involve significant effort from data experts, but with Report Builder, users can ask and answer questions flexibly. With access to all the data associated with their own environment, users can create custom reports with a blank canvas and all the fields that matter. This means that users can get new insights in minutes, without needing advanced analytics skills or support.
                  <br />
                  <br />
                  The user interface for Report Builder is designed to be simple and intuitive, making it easy to select the things that you want to count or see as a percentage, such as the number of high-risk calls or the percentage of calls under 60 seconds. Users can also select the fields they want to break the data down by, such as month, city, or agent. With this powerful feature, businesses can easily get the insights they need, leading to improved decision-making and better outcomes. daisee's commitment to an open data policy means that the data businesses send to daisee is still their data, which is enriched by machine learning models, giving users complete access and control over their data.
                </Copy>
                <a className='button' href={data.reportFlyer.publicURL} download>
                  <Button
                    action='Download the daisee REPORT BUILDER flyer'
                    btnHeight='auto'
                    bgColor={theme.colors.purple}
                  />
                </a>
              </CopyWrapper>
              <ImgWrapper>
                  <StyledImg style={{zIndex: 2}} shadow fluid={data.report.childImageSharp.fluid} alt="Report Builder" />
                  <ColourBlockRight sectionColor={Color.Yellow}/>
              </ImgWrapper>
            </IntroGrid>

            {/* DAISEE CHAT colour pink */}
            <IntroGrid>
              <ImgWrapper >
                <StyledImg style={{zIndex: 2}} shadow fluid={data.chatinsights.childImageSharp.fluid} alt="Chat Insights" />
                <Trapezium sectionColor={Color.Pink}/>
              </ImgWrapper>

              <CopyWrapper id='chat'>
                <Tag sectionColor={Color.Pink}>DAISEE CHAT</Tag>
                <H3>Maximize the potential of your chat interactions</H3>
                <Copy>
                  Over 80% of contact centres either use chat or plan to implement it. daisee Chat Insights allows customers to dive into the metrics that matter most in chat interactions. The Chat Insights capability helps customers keep track of their mix of phone versus chat-based interactions and shift towards more cost-effective interactions.
                  <br />
                  <br />
                  daisee offers customers on the Genesys Cloud Platform an omnichannel experience within the core daisee platform. The daisee platform allows you to easily toggle between voice and daisee Chat giving yet more insight and a more tightly integrated experience.
                  <br />
                  <br />
                  Interested in drilling down further on a specific chat? With daisee chat your QA can include:
                  <br />&nbsp; &bull; Consolidated information about your agents chat performance.
                    <br />&nbsp; &nbsp; &nbsp; &nbsp; &bull; An "All Chats" page to filter chats by date range and agent participants.
                  <br />&nbsp; &bull; Focus on individual chats
                    <br />&nbsp; &nbsp; &nbsp; &nbsp; &bull; Transcripts of chat conversations
                    <br />&nbsp; &nbsp; &nbsp; &nbsp; &bull; "Chat Player" where you can inspect individual chat metrics, browse the chat transcripts and search for terms within the chat transcripts.
                  <br />
                  <br />
                  We understand that businesses now want and need to understand the effects/abilities of their chat platforms in issue resolution. However, the way customers, bots and agents interact in a chat and the metrics used to measure the effectiveness of those chats are very different to voice/phone interactions. A chat can have multiple agents per chat and an agent can deal with multiple simultaneous chats. The chat dashboard allows daisee to provide an aggregated view of all chat activity between agents and customers, and the chat QA allows you to drill into specific chats based on those highlights.              
                </Copy>
              </CopyWrapper>
            </IntroGrid>
          </Section>
        </Container>
        <CTA />
        <MarqueeWrapper>
          <Marquee list={[...customers, ...partners]} title="Trusted by Our Customers and Partners" />
        </MarqueeWrapper>
      </Main>
      <Footer />
    </Site>
  );
};

const Emphasis = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.blue};
`;

const ColorStroke = styled.div`
  position: absolute;
  right: 25%;
  top: -10%;

  width: 150px;
  height: 500px;
  transform: skew(-20deg);
  background: ${({ theme }) => getGradient(theme.colors.info)};
  border-radius: 4px;

  @media all and (max-width: 48em) {
    top: -5%;
  }
`;

const MarqueeWrapper = styled.div`
  padding: 3rem 0;
  background: #fff;
`;
interface SectionProps {
  whitepaper?: boolean;
}

const Section = styled.section<SectionProps>`
  padding-top: 3rem;
  background: ${({ whitepaper }) => whitepaper && '#fff'};

  &#features {
    padding-top: 6rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 4rem;
  font-weight: 300;
  letter-spacing: -2px;
  color: ${({ theme }) => theme.colors.grey400};
  padding: 0 1.5rem 6rem;

  @media all and (max-width: 48em) {
    font-size: 3rem;
    padding: 0 0 3rem;
  }

  @media all and (max-width: 28.125em) {
    font-size: 2.5rem;
  }
`;

const Triangle = styled.div`
  position: absolute;
  right: -50%;
  top: -5%;
  transform: scaleX(-1);

  width: 0;
  height: 0;
  border-top: 200px solid transparent;
  border-left: 400px solid #6F37BD;
  border-bottom: 200px solid transparent;

  @media all and (max-width: 26.563em) {
    top: -25%;
  }
`;

const Trapezium = styled.div<ColorProps>`
  position: absolute;
  left: -15%;
  top: -10%;

  width: 150px;
  height: 500px;
  transform: skew(20deg);
  background: ${({ sectionColor }) => getColor(sectionColor)};
  border-radius: 4px;

  @media all and (max-width: 48em) {
    top: -50%;
    left: -30%;
  }

  @media all and (max-width: 48em) {
    top: -50%;
    left: -30%;
  }

  @media all and (max-width: 36.625em) {
    top: -125%;
    left: -50%;
  }
`;

const ColourBlock = styled.div<ColorProps>`
  position: absolute;
  width: 400px;
  height: 400px;
  background: ${({ sectionColor }) => getColor(sectionColor)};;
  border-radius: 50%;
  right: 50%;
  top: -10%;

  @media all and (max-width: 48em) {
    left: 70%;
  }
`;

const ColourBlockRight = styled.div<ColorProps>`
  position: absolute;
  width: 400px;
  height: 400px;
  background: ${({ sectionColor }) => getColor(sectionColor)};
  border-radius: 50%;
  left: 50%;
  top: -10%;

  @media all and (max-width: 48em) {
    right: 70%;
  }
`;

interface Video {
  loaded?: boolean;
}

const VideoWrapper = styled.div<Video>`
  position: relative;
  padding-bottom: 75%;
  height: 0;
  top: 0;
  transition: opacity 0.5s ease;

  &:before {
    position: absolute;
    transition: opacity 0.5s ease;
    content: '';
    width: 100%;
    height: 100%;
    background-color: #fff;

    z-index: 2;
    opacity: ${({ loaded }) => (loaded ? 0 : 1)};
  }

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
`;

interface ColorProps {
  sectionColor: Color | string;
}

const Tag = styled.p<ColorProps>`
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 1px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey400};
  user-select: none;
  height: fit-content;
  position: relative;
  padding-left: 1.5rem;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    height: 100%;
    background: ${({ sectionColor }) => getColor(sectionColor)};
    width: 4px;
  }
`;

const ImgWrapper = styled.div`
  position: relative;
`;

const HeroWrapper = styled.article`
  display: grid;
  gap: 1.5rem;
  grid-template-rows: min-content;
  padding: 5rem 1.5rem 0;

  @media all and (max-width: 48em) {
    padding: 6rem 0 0;
  }

  div:first-of-type {
    margin-top: 3rem;
  }
`;

export const CopyWrapper = styled.article`
  display: grid;
  gap: 1.5rem;
  grid-template-rows: min-content;
  position: relative;
  height: fit-content;
  margin: 0 1.5rem;

  a {
    text-decoration: underline;
    color: #d381ee;
  }

  @media all and (max-width: 48em) {
    padding: 0;
    grid-row: 1;
    margin: 0;
  }
`;

interface CopyProps {
  hero?: boolean;
  sectionColor?: Color;
}

export const Copy = styled.p<CopyProps>`
  font-size: ${({ hero }) => (hero ? '1.125rem' : '1rem')};
  font-weight: ${({ hero }) => (hero ? '500' : '400')};
  color: ${({ theme }) => theme.colors.grey700};
  max-width: 50ch;
  padding-left: ${({ sectionColor }) => sectionColor && '1.5rem'};
  position: relative;
  height: fit-content;
  line-height: 1.7;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    height: 100%;
    background: ${({ sectionColor }) => sectionColor && getColor(sectionColor)};
    width: 4px;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }

  @media all and (max-width: 26.563em) {
    font-size: 0.875rem;
  }
`;

const H1 = styled.h1`
  margin: 0;
  font-weight: 500;
  font-size: 2rem;
  letter-spacing: -1px;
  line-height: 1.4;

  @media all and (max-width: 28.125em) {
    font-size: 1.75rem;
  }
`;

const H3 = styled.h3`
  margin: 0;
  font-weight: 500;
  font-size: 2.25rem;
  letter-spacing: -1.5px;
  line-height: 1.2;

  @media all and (max-width: 28.125em) {
    font-size: 1.5rem;
  }
`;

interface IntroGridProps {
  whitepaper?: boolean;
  paddingBottom?: string;
}

const HeroGrid = styled.div<IntroGridProps>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  transition: all 0.75s ease;
  position: relative;
  height: fit-content;
  padding-top: 9rem;

  @media all and (max-width: 48em) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

export const IntroGrid = styled.div<IntroGridProps>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  padding-bottom: ${({ paddingBottom }) => paddingBottom ?? '12rem'};

  @media all and (max-width: 48em) {
    grid-template-columns: 1fr;
    padding-bottom: 6rem;
    gap: 3rem;
  }
`;

const IntroImgWrapper = styled.div`
  margin-right: -500px;

  @media all and (max-width: 48em) {
    margin-right: 0;
    padding: 0;
  }
`;

interface ImgProps {
  fluid: {};
  shadow?: boolean;
}

const StyledImg = styled(Img)<ImgProps>`
  box-shadow: ${({ theme, shadow }) => shadow && theme.boxShadow.large};
`;

export default Product;
